import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Spin, Alert } from 'antd';

// Get the API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const GoogleCallbackHandler = () => {
    const [status, setStatus] = useState('processing');
    const [errorMessage, setErrorMessage] = useState('');
    const location = useLocation();

    useEffect(() => {
        const processCallback = async () => {
            try {
                // Get query parameters
                const params = new URLSearchParams(location.search);
                const code = params.get('code');
                const state = params.get('state');
                const error = params.get('error');

                // Check for errors from Google
                if (error) {
                    setStatus('error');
                    setErrorMessage('Authorization failed: ' + error);
                    sendMessageToParent({ error: true, message: error });
                    return;
                }

                // Verify state token to prevent CSRF attacks
                const savedState = localStorage.getItem('googleAuthState');
                if (!state || state !== savedState) {
                    setStatus('error');
                    setErrorMessage('Invalid state parameter. This could be a security risk.');
                    sendMessageToParent({ error: true, message: 'Security validation failed' });
                    return;
                }

                // Clear state token from storage
                localStorage.removeItem('googleAuthState');

                if (!code) {
                    setStatus('error');
                    setErrorMessage('No authorization code received');
                    sendMessageToParent({ error: true, message: 'No authorization code received' });
                    return;
                }

                // Use API base URL for the callback
                const callbackUrl = `${API_BASE_URL}/callback-sso/check?code=${code}&state=${state}`;
                console.log('Using callback URL:', callbackUrl);

                const response = await fetch(callbackUrl, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    }
                });

                // Log response details for debugging
                console.log('Callback response status:', response.status);
                console.log('Content-Type:', response.headers.get('content-type'));

                if (!response.ok) {
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                        const errorData = await response.json();
                        setStatus('error');
                        setErrorMessage('Server error: ' + (errorData.error || response.statusText));
                    } else {
                        const errorText = await response.text();
                        console.error('Error response text:', errorText);
                        setStatus('error');
                        setErrorMessage('Server error: ' + response.statusText);
                    }
                    sendMessageToParent({ error: true, message: 'Server authentication failed' });
                    return;
                }

                const data = await response.json();
                console.log('Callback data received:', data);

                if (data && data.token && data.refresh_token) {
                    setStatus('success');
                    sendMessageToParent({
                        type: 'GOOGLE_AUTH_CALLBACK',
                        token: data.token,
                        refresh_token: data.refresh_token,
                        success: true
                    });
                } else {
                    setStatus('error');
                    setErrorMessage('Authentication successful, but no tokens received');
                    sendMessageToParent({ error: true, message: 'No tokens received' });
                }
            } catch (error) {
                console.error('Callback processing error:', error);
                setStatus('error');
                setErrorMessage('An unexpected error occurred: ' + error.message);
                sendMessageToParent({ error: true, message: 'Unexpected error' });
            }
        };

        processCallback();
    }, [location.search]);

    const sendMessageToParent = (data) => {
        if (window.opener) {
            window.opener.postMessage({ ...data, type: 'GOOGLE_AUTH_CALLBACK' }, window.location.origin);

            // Attempt to close window after sending message
            setTimeout(() => {
                if (window.opener) {
                    window.close();
                }
            }, 2000);
        }
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            padding: '20px'
        }}>
            {status === 'processing' && (
                <>
                    <Spin size="large" />
                    <p style={{ marginTop: '20px' }}>Processing authentication...</p>
                </>
            )}

            {status === 'success' && (
                <Alert
                    message="Authentication Successful"
                    description="You have successfully authenticated with Google. This window will close automatically."
                    type="success"
                    showIcon
                />
            )}

            {status === 'error' && (
                <Alert
                    message="Authentication Failed"
                    description={errorMessage || "An error occurred during authentication."}
                    type="error"
                    showIcon
                />
            )}
        </div>
    );
};

export default GoogleCallbackHandler;